import { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { DatePicker } from '@Components/DatePicker';
import { Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { Checkbox } from '@Components/FormCheck';
import { SEGMENT_NAME_FILTER_OPTIONS } from '../audience-penetration-constants';
import { ReportPeriod, SegmentTypeType } from '../audience-penetration-types';
import { AudiencePenetrationRowWithMetrics } from '../utils/filter';

interface AudiencePenetrationFiltersProps {
  availablePeriods: ReportPeriod[];
  selectedReportEndDate?: string;
  onPeriodChange: (value: string) => void;
  rows: AudiencePenetrationRowWithMetrics[];
  segmentTypeOptions: { value: string; label: string }[];
  segmentTypes: SegmentTypeType[];
  onSegmentTypesChange: (value: SegmentTypeType[]) => void;
  onNameFilterChange: (filter: (row: AudiencePenetrationRowWithMetrics) => boolean) => void;
  topN: number | null;
  onTopNChange: (value: number | null) => void;
  hideOverfullReach: boolean;
  onHideOverfullReachChange: (value: boolean) => void;
}

export const AudiencePenetrationFilters = ({
  availablePeriods,
  selectedReportEndDate,
  onPeriodChange,
  rows,
  segmentTypeOptions,
  segmentTypes,
  onSegmentTypesChange,
  onNameFilterChange,
  topN,
  onTopNChange,
  hideOverfullReach,
  onHideOverfullReachChange
}: AudiencePenetrationFiltersProps) => {
  // `reportStartDate` is already a month-start date — exactly the value DatePicker's
  // `fullMonthsOnly` mode selects/compares against. Only months with a matching available period
  // are selectable; picking one reports back its `reportEndDate` (what the data hook keys off).
  const reportEndDateByStartDate = useMemo(
    () => new Map(availablePeriods.map(period => [period.reportStartDate, period.reportEndDate])),
    [availablePeriods]
  );
  const selectedReportStartDate = availablePeriods.find(
    period => period.reportEndDate === selectedReportEndDate
  )?.reportStartDate;

  return (
    <div className='audience-penetration-filters'>
      {availablePeriods.length > 0 && (
        <DatePicker
          isSingle
          fullMonthsOnly
          range={
            selectedReportStartDate
              ? { start: selectedReportStartDate, end: selectedReportStartDate }
              : undefined
          }
          onChange={newRange => {
            const reportEndDate = reportEndDateByStartDate.get(newRange.start);
            if (reportEndDate) {
              onPeriodChange(reportEndDate);
            }
          }}
          isOutsideRange={date => !reportEndDateByStartDate.has(date)}
          fiscalCalOverride={{}}
          design={Design.NEUTRAL}
          placeholder='Select Month'
        />
      )}
      <div className='audience-penetration-segment-type-dropdown'>
        <Dropdown<SegmentTypeType>
          size={Size.LG}
          overlayPlacement={Placement.RIGHT}
          multiSelect
          stretch={StretchMode.FILL}
          options={segmentTypeOptions}
          value={segmentTypes}
          onChange={onSegmentTypesChange}
          placeholder='All Segment Types'
          allSelectedLabel='All Segment Types'
        />
      </div>
      <FilterBar
        size={Size.LG}
        lines={rows}
        options={SEGMENT_NAME_FILTER_OPTIONS}
        onFilter={onNameFilterChange}
        placeholder='Search segment name...'
      />
      <Checkbox
        id='hide-overfull-reach'
        label='Hide Audience Reach % over 100%'
        checked={hideOverfullReach}
        onChange={e => onHideOverfullReachChange(e.target.checked)}
      />
      <Form.Group className='top-n-input'>
        <Form.Label>Top N</Form.Label>
        <Form.Control
          type='number'
          size='sm'
          min={0}
          placeholder='All'
          value={topN ?? ''}
          onChange={e => onTopNChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      </Form.Group>
    </div>
  );
};
